import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Colors, Spacing } from '@/shared/theme';
import { Button, Title, Text, CenteredColumn } from '@/shared/ui';
import { Modal, type ModalCloseMethod } from '@/shared/Modal';
import { useAnalytics } from '@/hooks/useAnalytics';
import { chatBotManager } from '@/services/chatBotManager';
import { env } from '@/env';
import {
  ASK_HELP,
  CLOSE_HELP_MENU,
  EVENT_SOURCES,
  OPEN_HELP_MENU,
  type EventSource,
} from '@/consts/events';
import { WELCOME_STEP_NAME } from '@/components/webreg/wregSteps';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: EventSource;
  stepName?: string;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  width: 100%;
`;

const Divider = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${Spacing.x2}px;
  margin: ${Spacing.x1}px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${Colors.foregroundDisabledAlt};
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: ${Spacing.x2}px;
  width: 100%;
  margin-top: ${Spacing.x1}px;

  /* Make buttons take equal width and stretch */
  & > button {
    flex: 1;
    width: auto;
  }
`;

const ChatbotButtonContainer = styled.div`
  width: 100%;
  margin-top: ${Spacing.x2}px;
`;

const SupportModal: React.FC<SupportModalProps> = ({
  isOpen,
  onClose,
  source,
  stepName = WELCOME_STEP_NAME,
}) => {
  const { t } = useTranslation();
  const { track, trackPopupView } = useAnalytics();

  // trackPopupView, not trackView, in the deps — the effect body calls the
  // former. Both are identity-stable today and are byte-identical wrappers over
  // `track` (the old view_/popup_ prefixing is gone; the split survives only so
  // call sites can signal intent), but a deps list that doesn't match the body is
  // how a stale closure gets introduced later (PR #101 review).
  useEffect(() => {
    if (isOpen) {
      trackPopupView({ event: OPEN_HELP_MENU, properties: { step_name: stepName, source } });
    }
  }, [isOpen, source, stepName, trackPopupView]);

  const askHelp = (method: 'chat' | 'phone') =>
    track({
      event: ASK_HELP,
      properties: { method, source: EVENT_SOURCES.HELP_MENU, step_name: stepName },
    });

  const handlePhoneClick = () => {
    askHelp('phone');
    window.location.href = `tel:${env.SUPPORT_NUMBER}`;
  };

  const handleChatbotClick = () => {
    askHelp('chat');
    chatBotManager.open();
    onClose();
  };

  const handleClose = (method: ModalCloseMethod = 'close_button') => {
    track({ event: CLOSE_HELP_MENU, properties: { method, step_name: stepName } });
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      ariaLabelledBy="support-modal-title"
      ariaDescribedBy="support-modal-description"
    >
      <ModalContent>
        <Title size="h4" bold centered id="support-modal-title">
          {t('modals.support-modal.title')}
        </Title>
        <Text size="p2" centered id="support-modal-description">
          {t('modals.support-modal.subtitle')}
        </Text>

        <ChatbotButtonContainer>
          <CenteredColumn span={6}>
            <Button
              variant="action-primary"
              text={t('modals.support-modal.cta-chatbot')}
              onClick={handleChatbotClick}
            />
          </CenteredColumn>
        </ChatbotButtonContainer>

        <Divider aria-hidden="true">
          <Text size="p3">{t('modals.support-modal.or')}</Text>
        </Divider>
        <Text size="p3" centered>
          {t('modals.support-modal.human-operating-hours')}
        </Text>
        <ButtonsRow>
          <Button
            size="xsmall"
            variant="action-secondary"
            text={t('modals.support-modal.cta-phone')}
            onClick={handlePhoneClick}
          />
        </ButtonsRow>
      </ModalContent>
    </Modal>
  );
};

export { SupportModal };
